<template>
  <div class="covariance-matrix-root" :class="{ isExpanded }">
    <expand-icon
      class="chart-expand-icon"
      @click="handleExpandChart"
      v-if="!isExpanded"
    />
    <v-chart class="covariance-matrix" :options="chartOptions" autoresize />
  </div>
</template>

<script>
import {
  GridComponent,
  TooltipComponent,
  VisualMapComponent,
} from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";
import ECharts from "vue-echarts";
import ExpandIcon from "vue-material-design-icons/ArrowExpand.vue";
import { HeatmapChart } from "echarts/charts";
import { mapMutations } from "vuex";
import { use } from "echarts/core";

use([
  GridComponent,
  TooltipComponent,
  VisualMapComponent,
  HeatmapChart,
  CanvasRenderer,
]);

export default {
  name: "covariance-matrix",
  components: {
    "v-chart": ECharts,
    ExpandIcon,
  },
  props: {
    data: Object,
    displayTickers: Array,
    isExpanded: Boolean,
  },
  data() {
    return {
      chartOptions: {
        tooltip: {
          position: "top",
        },
        grid: {
          height: "80%",
          width: "75%",
          top: 20,
        },
        xAxis: {
          type: "category",
          position: "top",
          splitArea: {
            show: true,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          type: "category",
          splitArea: {
            show: true,
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        visualMap: {
          calculable: true,
          orient: "vertical",
          right: 0,
          bottom: "18%",
          align: "left",
          itemHeight: "246px",
          inRange: {
            color: ["#E8F7FF", "#0255D1"],
          },
          precision: 2,
        },
        series: [
          {
            type: "heatmap",
            data: [],
            label: {
              show: true,
            },
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowColor: "rgba(0, 0, 0, 0.5)",
              },
            },
          },
        ],
      },
    };
  },
  methods: {
    ...mapMutations("portfolioManagerStore", ["setExpandedChart"]),
    populateData() {
      const heads = Object.keys(this.data).filter((ticker) =>
        this.displayTickers.map((item) => item.ticker).includes(ticker)
      );
      this.chartOptions.yAxis.data = heads;
      this.chartOptions.xAxis.data = heads;
      let covData = [];
      let pairs = [];
      heads.forEach((head) => {
        Object.entries(this.data[head]).forEach((ticker) => {
          if (
            pairs.includes([head, ticker[0]]) ||
            pairs.includes([ticker[0], head]) ||
            !heads.includes(ticker[0])
          ) {
            return null;
          } else {
            pairs.push([head, ticker[0]]);
            covData.push([head, ticker[0], ticker[1].toFixed(2)]);
          }
        });
      });
      this.chartOptions.series[0].data = [...covData].reverse();
      this.chartOptions.visualMap.min = Math.min(
        ...covData.map((el) => parseFloat(el[2]))
      );
      this.chartOptions.visualMap.max = Math.max(
        ...covData.map((el) => parseFloat(el[2]))
      );
    },
    handleExpandChart() {
      this.setExpandedChart({
        type: "covariance",
        data: {
          data: this.data,
          displayTickers: this.displayTickers,
        },
      });
      this.$bvModal.show("expanded-chart");
    },
  },
  mounted() {
    this.populateData();
  },
  watch: {
    displayTickers() {
      this.populateData();
    },
  },
};
</script>

<style lang="scss" scoped>
.covariance-matrix-root {
  position: relative;
  .chart-expand-icon {
    left: -20px;
  }
  &:hover {
    .chart-expand-icon {
      display: block;
      opacity: 0.75;
    }
  }
  .covariance-matrix {
    max-height: 320px;
    max-width: 100%;
  }
  &.isExpanded {
    .covariance-matrix {
      max-height: 70vh;
      height: 70vh;
      width: 100%;
      max-width: 100%;
    }
  }
}
</style>